import React from "react";
import BaseController from "../../common/base/BaseController";
import { showToast } from "../../common/utils/ControllerUtils";
import routes from "../../navigation/routes";
import { navigationRef } from "../../../App";
import Logger from "../../common/utils/Logger";
import { setupSteps } from "../data/setupSteps";
import NtOnboardingLayout from "../../onboarding/components/NtOnboardingLayout";
import NtOnboardingTopRail from "../../onboarding/components/NtOnboardingTopRail";
import NtOnboardingStepper from "../../onboarding/components/NtOnboardingStepper";
import NtOnboardingHeader from "../../onboarding/components/NtOnboardingHeader";
import NtOnboardingFooter from "../../onboarding/components/NtOnboardingFooter";
import NtSetupWelcomeStep from "../components/steps/NtSetupWelcomeStep";
import NtSetupAssistantStep from "../components/steps/NtSetupAssistantStep";
import NtSetupExtensionsStep from "../components/steps/NtSetupExtensionsStep";
import NtSetupRingGroupsStep from "../components/steps/NtSetupRingGroupsStep";
import NtSetupIvrStep from "../components/steps/NtSetupIvrStep";
import NtSetupPhoneNumberStep from "../components/steps/NtSetupPhoneNumberStep";

const STEP_COPY = {
  welcome: {
    title: "Let's get your phone system live.",
    subtitle:
      "A few quick steps to set up extensions for your team, route inbound calls with ring groups, build an auto-attendant, and reserve a phone number. You can skip any step and finish it later from the admin.",
  },
  assistant: {
    title: "Want an AI to design your starter setup?",
    subtitle:
      "Tell us a bit about the business and we'll propose extensions, ring groups, and an auto-attendant. You'll review the plan before anything is created — or skip to set things up by hand.",
  },
  extensions: {
    title: "Add your team's extensions.",
    subtitle:
      "Each person gets a 4-digit extension. They'll get an email invite to set up their device or use the web softphone.",
  },
  ringgroups: {
    title: "Set up ring groups.",
    subtitle:
      "Send incoming calls to a team of extensions at once — useful for Sales, Support or the front desk. Skip if you don't need them yet.",
  },
  ivr: {
    title: "Configure an IVR menu.",
    subtitle:
      "Build a \"Press 1 for sales, 2 for support…\" auto-attendant. Record a greeting later — paste TTS text for now.",
  },
  phonenumber: {
    title: "Pick your phone number.",
    subtitle:
      "This is the number people will dial to reach you. Pick a local area code or go toll-free — you can buy more numbers later from the admin.",
  },
};

class SetupController extends BaseController {
  state = {
    ...super.state,
    currentStep: 0,
    phoneSearch: { country: "United States", pattern: "", type: "local" },
    selectedNumber: null,
  };

  componentDidMount() {
    super.componentDidMount();
    document.title = "Voxcraft | Setup Wizard";
  }

  handleSelectStep = (index) => {
    this.setState({ currentStep: index });
  };

  handleBack = () => {
    this.setState((s) => ({ currentStep: Math.max(0, s.currentStep - 1) }));
  };

  handleContinue = () => {
    const { currentStep } = this.state;
    if (currentStep === setupSteps.length - 1) {
      this.handleFinish();
      return;
    }
    this.setState({ currentStep: currentStep + 1 });
  };

  handleSkip = () => this.handleContinue();

  handleClose = () => {
    if (navigationRef.current) {
      navigationRef.current.navigate(routes.dashboard);
    }
  };

  handleFinish = () => {
    // TODO: when the phone-number purchase API exists, reserve the selected
    // number here before navigating. For now we just log + redirect.
    Logger("Setup wizard · finished", {
      selectedNumber: this.state.selectedNumber,
      phoneSearch: this.state.phoneSearch,
    });
    showToast(
      "You're all set",
      "Setup complete. Welcome to your dashboard.",
      "success"
    );
    if (navigationRef.current) {
      navigationRef.current.navigate(routes.dashboard);
    }
  };

  renderStep = () => {
    const key = setupSteps[this.state.currentStep].key;
    if (key === "welcome") return <NtSetupWelcomeStep />;
    if (key === "assistant") return <NtSetupAssistantStep />;
    if (key === "extensions") return <NtSetupExtensionsStep />;
    if (key === "ringgroups") return <NtSetupRingGroupsStep />;
    if (key === "ivr") return <NtSetupIvrStep />;
    if (key === "phonenumber") {
      return (
        <NtSetupPhoneNumberStep
          search={this.state.phoneSearch}
          selectedNumber={this.state.selectedNumber}
          onChangeSearch={(phoneSearch) => this.setState({ phoneSearch })}
          onSelect={(selectedNumber) => this.setState({ selectedNumber })}
        />
      );
    }
    return null;
  };

  render() {
    const { currentStep } = this.state;
    const copy = STEP_COPY[setupSteps[currentStep].key];
    return (
      <NtOnboardingLayout
        top={
          <>
            <NtOnboardingTopRail
              steps={setupSteps}
              currentStep={currentStep}
            />
            <NtOnboardingStepper
              steps={setupSteps}
              currentStep={currentStep}
              onSelectStep={this.handleSelectStep}
            />
          </>
        }
        body={
          <>
            <NtOnboardingHeader title={copy.title} subtitle={copy.subtitle} />
            {this.renderStep()}
          </>
        }
        footer={
          <NtOnboardingFooter
            steps={setupSteps}
            currentStep={currentStep}
            canContinue={true}
            finishLabel="Finish setup"
            onBack={this.handleBack}
            onSkip={this.handleSkip}
            onContinue={this.handleContinue}
            onExit={this.handleClose}
          />
        }
      />
    );
  }
}

export default SetupController;
